import type { Annotation } from "@/types"
import {
  Badge,
  Button,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"
import { Users } from "lucide-react"

import { cn } from "@/utils/cn"

interface CategoricalHumanFeedbackButtonProps {
  annotation: Annotation | null
  onValueChange: (
    floatValue: number | null,
    stringValue: string | null,
    setValue: string[] | null,
  ) => void
  onExplanationChange: (explanation: string | null) => void
  otherUserAnnotations?: Annotation[]
  categories: string[]
  categoryColors?: Record<string, string>
}

export const CategoricalHumanFeedbackButton = ({
  annotation,
  onValueChange,
  onExplanationChange: _onExplanationChange,
  otherUserAnnotations,
  categories,
  categoryColors,
}: CategoricalHumanFeedbackButtonProps) => {
  const currentValue = annotation?.ground_truth_string_value ?? null
  const hasOtherAnnotations =
    otherUserAnnotations && otherUserAnnotations.length > 0

  return (
    <div className="flex flex-col gap-1.5">
      <Label>Human Review</Label>
      <div className="flex items-start justify-end gap-1.5">
        <Select
          value={currentValue ?? ""}
          onValueChange={(val) => {
            if (val === currentValue) {
              onValueChange(null, null, null)
            } else {
              onValueChange(null, val, null)
            }
          }}
        >
          <SelectTrigger
            variant="secondary"
            aria-label="Select category"
            className={cn(
              "transition-all duration-200",
              currentValue
                ? cn("font-medium", categoryColors?.[currentValue])
                : "text-coval-text-muted",
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent aria-label="Select category">
            {categories.map((category) => (
              <SelectItem key={category} value={category}>
                {category}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="secondary"
              disabled={!hasOtherAnnotations}
              aria-label="View other user annotations"
              className="transition-all duration-200"
              onClick={(e) => e.stopPropagation()}
            >
              <Users />
            </Button>
          </PopoverTrigger>
          <PopoverContent side="top" className="w-64 p-2">
            <div className="max-h-48 overflow-y-auto">
              {otherUserAnnotations?.map((otherAnnotation) => {
                const value = otherAnnotation.ground_truth_string_value
                return (
                  <div
                    key={otherAnnotation.id}
                    className="flex items-center justify-between py-1 px-1"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      {value ? (
                        <Badge>{value}</Badge>
                      ) : (
                        <span className="text-sm font-medium text-coval-text-muted">
                          N/A
                        </span>
                      )}
                      <span className="text-xs text-coval-text-muted">
                        {otherAnnotation.assignee}
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  )
}
